// ActorIntegrityReport — تقرير "سلامة المنفّذ" للأدمن (مقترح 3.2.0، مكمّل لـ N6).
// يساعد مدير النظام: "هل أثق بسجل التدقيق؟ أين الثغرات؟ ماذا أفعل؟"
//  درجة ثقة + تصنيف المشاكل + تنقيب لعيّنة السجلّات + بصمة المحذوفين + تصدير Excel.
// موحّدة على مجموعة مكوّنات SmartPage + ثيم SMART_COLORS (3.4.0 / N13).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress,
  Collapse, Divider, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { exportStyledWorkbook } from '../../utils/xlsxStyled';
import { SmartPage, SmartHeader, SmartMetricGrid, SmartTableCard } from '../../components/smart';
import { SMART_COLORS as C } from '../../theme/smartStableTheme';

export default function ActorIntegrityReport() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/audit/actor-integrity');
      setData(res.data);
      setExpanded(new Set());
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const items = data?.items || [];
  const deletedUsers = data?.deletedUsersReferenced || [];
  const score = totals.score ?? 100;
  const scoreTone = score >= 99 ? 'ok' : score >= 90 ? 'warn' : 'bad';

  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const metricItems = useMemo(() => ([
    { key: 'score', label: t('actorIntegrity.score', 'درجة الثقة'), value: `${score}%`, tone: scoreTone, accent: true, hint: t('actorIntegrity.scoreHint', 'نسبة خلايا المنفّذ الموثوقة'), xs: 12, md: 4 },
    { key: 'tables', label: t('actorIntegrity.tables', 'جداول مفحوصة'), value: totals.tablesScanned ?? 0, tone: 'neutral', xs: 6, md: 2 },
    { key: 'cells', label: t('actorIntegrity.scannedCells', 'سجلّات مفحوصة'), value: totals.scannedCells ?? 0, tone: 'neutral', xs: 6, md: 2 },
    { key: 'null', label: t('actorIntegrity.nullActors', 'منفّذ فارغ'), value: totals.nullActors ?? 0, tone: totals.nullActors ? 'warn' : 'ok', xs: 6, md: 2 },
    { key: 'orphan', label: t('actorIntegrity.orphanActors', 'مستخدم محذوف'), value: totals.orphanActors ?? 0, tone: totals.orphanActors ? 'bad' : 'ok', xs: 6, md: 2 },
  ]), [t, score, scoreTone, totals]);

  const exportRows = useMemo(() => items.filter((i) => !i.error).map((i) => ({
    Table: i.table, Column: i.column, Total: i.total,
    'Empty actor': i.nullActor, 'Deleted user': i.orphanActor, 'Rate %': i.issuePct,
    'Sample record IDs': (i.sampleNullIds || []).join(', '),
    'Referenced deleted users': (i.orphanUserIds || []).join(', '),
  })), [items]);

  const handleExport = async () => {
    await exportStyledWorkbook({
      fileName: `actor-integrity-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: t('actorIntegrity.title', 'سلامة المنفّذ'),
      rows: exportRows.length ? exportRows : [{ '-': '-' }],
      subtitle: `${t('actorIntegrity.score', 'درجة الثقة')}: ${score}%`,
    });
  };

  return (
    <SmartPage>
      <SmartHeader
        title={t('actorIntegrity.title', 'سلامة المنفّذ')}
        subtitle={t('actorIntegrity.subtitle', 'سجلّات بلا منفّذ مسجَّل (NULL) أو تشير لمستخدم محذوف — نقاط عمياء في التدقيق.')}
        actions={
          <>
            <Button onClick={handleExport} startIcon={<DownloadIcon />} variant="outlined" disabled={!data}
              sx={{ color: C.gold, borderColor: C.gold }}>{t('export', 'تصدير')}</Button>
            <Button onClick={load} startIcon={<RefreshIcon />} variant="contained"
              sx={{ bgcolor: C.gold, '&:hover': { bgcolor: C.goldHover } }}>{t('refresh', 'تحديث')}</Button>
          </>
        }
      />

      {/* لوحة الإرشاد: ماذا يعني التقرير وكيف يفيد مدير النظام */}
      <Accordion variant="outlined" sx={{ mb: 2, borderColor: C.line, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon sx={{ color: C.gold }} />
            <Typography sx={{ fontWeight: 800, color: C.text }}>{t('actorIntegrity.guideTitle', 'كيف يفيدك هذا التقرير؟')}</Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ color: C.text, mb: 1 }}>
            <b>{t('actorIntegrity.nullActors', 'منفّذ فارغ')}:</b> {t('actorIntegrity.guideNull', 'سجلّات أُنشئت/عُدّلت بلا منفّذ — غالبًا قبل تفعيل فرض المصادقة أو عبر عملية نظام. تحقّق منها عبر معرّفات العيّنة.')}
          </Typography>
          <Typography variant="body2" sx={{ color: C.text, mb: 1 }}>
            <b>{t('actorIntegrity.orphanActors', 'مستخدم محذوف')}:</b> {t('actorIntegrity.guideOrphan', 'المنفّذ يشير لحساب حُذف لاحقًا — مهم للمراجعة (مَن غيّر هذا السجل؟). راجع بصمة المستخدمين المحذوفين أدناه.')}
          </Typography>
          <Typography variant="body2" sx={{ color: C.text }}>
            <b>{t('actorIntegrity.guideActionTitle', 'الإجراء')}:</b> {t('actorIntegrity.guideAction', 'كلّما اقتربت درجة الثقة من 100٪ كان سجل التدقيق أوثق. للسجلّات القديمة الفارغة: قرّر قبولها كإرث أو نسبها يدويًّا. وتأكّد أن المصادقة مفروضة على كل المسارات (N6) لمنع تكرارها.')}
          </Typography>
        </AccordionDetails>
      </Accordion>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress sx={{ color: C.gold }} /></Box>
      ) : data ? (
        <>
          <SmartMetricGrid items={metricItems} sx={{ mb: 3 }} />

          {deletedUsers.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: C.line }}>
              <Typography sx={{ fontWeight: 800, color: C.text, mb: 1 }}>{t('actorIntegrity.deletedUsers', 'بصمة المستخدمين المحذوفين')}</Typography>
              <Typography variant="caption" sx={{ color: C.muted, display: 'block', mb: 1 }}>
                {t('actorIntegrity.deletedUsersHint', 'حسابات محذوفة ما زالت مُشارًا إليها كمنفّذ (المعرّف × عدد السجلّات).')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {deletedUsers.map((d) => (
                  <Chip key={d.userRef} label={`${d.userRef} × ${d.count}`} sx={{ bgcolor: C.roseSoft, color: C.rose, fontWeight: 700 }} />
                ))}
              </Stack>
            </Paper>
          )}

          {items.length === 0 ? (
            <Alert severity="success">{t('actorIntegrity.clean', 'لا مشاكل: كل السجلّات لها منفّذ صالح.')}</Alert>
          ) : (
            <SmartTableCard>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: C.cardSoft }}>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell sx={{ fontWeight: 900 }}>{t('actorIntegrity.table', 'الجدول')}</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>{t('actorIntegrity.column', 'العمود')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900 }}>{t('actorIntegrity.total', 'الإجمالي')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900 }}>{t('actorIntegrity.nullActors', 'منفّذ فارغ')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900 }}>{t('actorIntegrity.orphanActors', 'مستخدم محذوف')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 900 }}>{t('actorIntegrity.issuePct', 'النسبة')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it, i) => {
                      const key = `${it.table}.${it.column}.${i}`;
                      const open = expanded.has(key);
                      const hasDetail = !it.error && ((it.sampleNullIds || []).length > 0 || (it.orphanUserIds || []).length > 0);
                      return (
                        <React.Fragment key={key}>
                          <TableRow hover sx={{ cursor: hasDetail ? 'pointer' : 'default' }} onClick={() => hasDetail && toggle(key)}>
                            <TableCell>{hasDetail && <IconButton size="small">{open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</IconButton>}</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: C.text }}>{it.table}</TableCell>
                            <TableCell sx={{ color: C.muted }}>{it.column}</TableCell>
                            {it.error ? (
                              <TableCell colSpan={4}><Chip size="small" label={it.error} /></TableCell>
                            ) : (
                              <>
                                <TableCell align="center">{it.total}</TableCell>
                                <TableCell align="center">{it.nullActor ? <Chip size="small" label={it.nullActor} sx={{ bgcolor: C.warning, color: '#fff', fontWeight: 700 }} /> : 0}</TableCell>
                                <TableCell align="center">{it.orphanActor ? <Chip size="small" label={it.orphanActor} sx={{ bgcolor: C.rose, color: '#fff', fontWeight: 700 }} /> : 0}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 700 }}>{it.issuePct}%</TableCell>
                              </>
                            )}
                          </TableRow>
                          {hasDetail && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                                <Collapse in={open} timeout="auto" unmountOnExit>
                                  <Box sx={{ py: 1.5, px: 2, bgcolor: C.cardSoft }}>
                                    {(it.sampleNullIds || []).length > 0 && (
                                      <Box sx={{ mb: 1 }}>
                                        <Typography variant="caption" sx={{ color: C.muted, fontWeight: 700 }}>
                                          {t('actorIntegrity.sampleRecords', 'عيّنة معرّفات السجلّات الفارغة')} ({it.pk}):
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                          {it.sampleNullIds.map((id) => <Chip key={String(id)} size="small" label={String(id)} variant="outlined" />)}
                                        </Stack>
                                      </Box>
                                    )}
                                    {(it.orphanUserIds || []).length > 0 && (
                                      <Box>
                                        <Typography variant="caption" sx={{ color: C.muted, fontWeight: 700 }}>
                                          {t('actorIntegrity.referencedUsers', 'مستخدمون محذوفون مُشار إليهم')}:
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                          {it.orphanUserIds.map((v) => <Chip key={String(v)} size="small" label={String(v)} sx={{ bgcolor: C.roseSoft, color: C.rose }} />)}
                                        </Stack>
                                      </Box>
                                    )}
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </SmartTableCard>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" sx={{ color: C.muted }}>
            {t('actorIntegrity.generatedAt', 'تاريخ اللقطة')}: {data.generatedAt ? new Date(data.generatedAt).toLocaleString() : '—'}
          </Typography>
        </>
      ) : null}
    </SmartPage>
  );
}
